import type {Repository} from '@github-ui/current-repository'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {actionsWorkflowRunPath, repoAttestationDownloadPath, repoAttestationShowPath} from '@github-ui/paths'
import {DownloadIcon, VerifiedIcon} from '@primer/octicons-react'
import {IconButton, Link, RelativeTime} from '@primer/react'
import pluralize from 'pluralize'

import type {Attestation} from '../types'
import styles from './AttestationsList.module.css'

type AttestationsListItemProps = {
  repo: Repository
  attestation: Attestation
  selected: boolean
  onSelect: () => void
}

export function AttestationListItem({repo, attestation, selected, onSelect}: AttestationsListItemProps) {
  const [_, runId, attempt] = attestation.runInvocationUri.match(/\/actions\/runs\/(\d+)\/attempts\/(\d+)$/) || []
  const subjectName = attestation.subjects[0]?.subjectName || '[unnamed subject]'
  const subjectCountMinusOne = attestation.subjectsCount - 1
  const othersText = pluralize('others', subjectCountMinusOne)
  const listItemTitle =
    subjectCountMinusOne === 0 ? subjectName : `${subjectName} and ${subjectCountMinusOne} ${othersText}`
  const interpunct = ' · '

  return (
    <ListItem
      key={attestation.id}
      onSelect={onSelect}
      isSelected={selected}
      title={
        <ListItemTitle
          href={repoAttestationShowPath({
            repo,
            attestationId: attestation.id,
          })}
          anchorClassName="fgColor-default"
          containerClassName={styles.ListItemTitle_Truncated}
          value={listItemTitle}
        />
      }
      metadata={
        <ListItemMetadata>
          <IconButton
            aria-label={`Download attestation #${attestation.id}`}
            icon={DownloadIcon}
            data-turbo="false"
            target="_blank"
            data-testid="attestation-download-link"
            variant="invisible"
            as="a"
            href={repoAttestationDownloadPath({
              repo,
              attestationId: attestation.id,
            })}
          />
        </ListItemMetadata>
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual>
          <VerifiedIcon size={16} className={'mr-2 fgColor-success'} verticalAlign={'middle'} />
        </ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription>
          <ListItemDescriptionItem className="fgColor-muted">
            {`#${attestation.id}`}
            {interpunct}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem>
            {`${attestation.subjectsCount} `}
            {pluralize('subjects', attestation.subjectsCount)}
            {interpunct}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem>
            {attestation.predicateType}
            {interpunct}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem>
            <RelativeTime data-testid="attestation-list-item-date" datetime={attestation.createdAt} prefix="" /> by
          </ListItemDescriptionItem>
          <ListItemDescriptionItem className="text-semibold">
            <Link
              className="fgColor-default"
              href={actionsWorkflowRunPath({
                repo: repo.name,
                owner: repo.ownerLogin,
                runId,
                attempt,
              })}
              data-testid="attestation-workflow-link"
            >
              {attestation.buildConfigDisplayName.replace(/^\.github\/workflows\//, '')}@
              {attestation.sourceRepositoryRef}
            </Link>
          </ListItemDescriptionItem>
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

try{ AttestationListItem.displayName ||= 'AttestationListItem' } catch {}